import { useAuth } from '~/composables/useAuth'
import { useCurrentLocationAccount } from '~/composables/useCurrentLocationAccount'
import { useCurrentOperator } from '~/composables/useCurrentOperator'
import { useCurrentTargetDate } from '~/composables/useCurrentTargetDate'
import { useSessionStorage } from '~/composables/useSessionStorage'
import { useSupabaseUser } from '~/composables/useSupabaseUser'
import { getCheckFile } from '~/repositories/checkFileRepository'
import { isIpNotAllowedPostgrestError } from '~/repositories/error'
import { currentBusinessDate, parseDate } from '~/utils/date'
import { showIpNotAllowedError } from '~/utils/error'

export default defineNuxtRouteMiddleware(async (to, _from) => {
  try {
    const { recheckSession } = useAuth()
    await recheckSession()

    const supabaseUser = useSupabaseUser()

    // トップページであればリダイレクト先の指定はなくてOK
    const redirectTo = to.fullPath === '/' ? undefined : to.fullPath

    // 未ログイン状態の場合はlogin画面に転送
    if (!supabaseUser.value && to.name !== 'login') {
      // 未ログインのためsession storageを初期化
      const { clear } = useSessionStorage()
      clear()

      return navigateTo({ name: 'login', query: { redirect: redirectTo } })
    }

    // ログイン状態の場合
    if (supabaseUser.value) {
      // その他stateを必要に応じて復元
      const { currentLocationAccount, recoverCurrentLocationAccount } = useCurrentLocationAccount()
      const { currentOperator } = useCurrentOperator()
      const { currentTargetDate } = useCurrentTargetDate()

      if (!currentLocationAccount.value) {
        await recoverCurrentLocationAccount()
      }

      // 名前付きルートの params は省略すると遷移元のルートから引き継がれるが、
      // 直リンクやリロード時の遷移元は params を持たないため明示的に渡す
      const organizationId =
        currentLocationAccount.value?.organization.id ?? to.params.organizationId

      if (to.name === 'organizationId-checkFiles-select') {
        // リダイレクトする必要がないページはそのまま
        return
      }

      // これ以降のリダイレクトは、状態によって遷移したらまずいケースをケアするためのもの
      if (!currentOperator.value) {
        return navigateTo({
          name: 'organizationId-checkFiles-select',
          params: { organizationId },
          query: { redirect: redirectTo },
        })
      }
      if (to.name?.toString() === 'index' || to.name?.toString() === 'login') {
        return navigateTo({
          name: 'organizationId-checkFiles-select',
          params: { organizationId },
          query: { redirect: redirectTo },
        })
      }
      if (to.name?.toString().startsWith('organizationId-checkFiles-checkFileId')) {
        if (currentTargetDate.value) {
          if (!parseDate(currentTargetDate.value).isSame(currentBusinessDate(), 'day')) {
            // 日付またぎのタイミングでチェックファイル選択画面へリダイレクト
            currentTargetDate.value = currentBusinessDate().format('YYYY-MM-DD')
            return navigateTo({
              name: 'organizationId-checkFiles-select',
              params: { organizationId },
            })
          }
        }

        const { checkFileId } = to.params
        const checkFile = await getCheckFile(Number(checkFileId))
        if (!checkFile) {
          // チェックファイルが存在しない場合はチェックファイル選択画面へリダイレクト
          return navigateTo({
            name: 'organizationId-checkFiles-select',
            params: { organizationId },
          })
        }

        if (to.name?.toString().startsWith('organizationId-checkFiles-checkFileId-workFiles')) {
          if (checkFile.type === 'AS_NEEDED') {
            // AS_NEEDED タイプのチェックファイルは、チェックファイル選択画面へリダイレクト
            return navigateTo({
              name: 'organizationId-checkFiles-checkFileId-asNeededWorkFiles',
              params: { organizationId, checkFileId },
            })
          }
        }
        if (
          to.name?.toString().startsWith('organizationId-checkFiles-checkFileId-asNeededWorkFiles')
        ) {
          if (checkFile.type === 'SCHEDULED') {
            return navigateTo({
              name: 'organizationId-checkFiles-checkFileId-yyyymmdd',
              params: {
                organizationId,
                checkFileId,
                yyyymmdd: currentBusinessDate().format('YYYY-MM-DD'),
              },
            })
          }
        }
      }
      currentTargetDate.value = currentBusinessDate().format('YYYY-MM-DD')
    }
  } catch (e) {
    // app初期マウント前, ナビゲーション時のIPアドレス制限エラーをハンドリングする
    if (isIpNotAllowedPostgrestError(e)) {
      showIpNotAllowedError()
      return
    }
    throw e
  }
})
